import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import {
  IQueryParams,
  createEntitySlice,
  EntityState,
  serializeAxiosError,
  IOption,
  BaseResponseEntities,
} from 'app/shared/reducers/reducer.utils';
import { IEmployee, defaultValue } from 'app/shared/model/employee.model';
import { apiOkr } from 'app/config/axios-interceptor';

const initialState: EntityState<IEmployee> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/okr/party';

// Actions

export const getEntities = createAsyncThunk(
  'employee/fetch_entity_list',
  async ({ searchText, page, size, sort, filters }: IQueryParams) => {
    return apiOkr.get<IEmployee[]>(apiUrl, {
      params: {
        searchText,
        page,
        size,
        filters: filters && JSON.stringify(filters),
        sort: sort && sort,
        // cacheBuster: new Date().getTime(),
      },
    });
  },
);

export const getAllEntities = createAsyncThunk(
  'employee/fetch_all_entity_list',
  async (id?: string) => {
    if (id) return apiOkr.get<IOption[]>(`${apiUrl}/all?id=${id}`);
    return apiOkr.get<IOption[]>(`${apiUrl}/all`);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'employee/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return apiOkr.get<IEmployee>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'employee/create_entity',
  async (entity: IEmployee, thunkAPI) => {
    const result = await apiOkr.post<IEmployee>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'employee/update_entity',
  async (entity: IEmployee, thunkAPI) => {
    const result = await apiOkr.put<IEmployee>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'employee/partial_update_entity',
  async (entity: IEmployee, thunkAPI) => {
    const result = await apiOkr.patch<IEmployee>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'employee/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await apiOkr.delete<IEmployee>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const EmployeeSlice = createEntitySlice({
  name: 'employee',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })

      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(getAllEntities), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          options: data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entities = [];
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = EmployeeSlice.actions;

// Reducer
export default EmployeeSlice.reducer;
